// ============================================================
// SoundEngine — Web Audio API synthesizer
// All sounds are procedurally generated — no files needed!
// ============================================================

export class SoundEngine {
  constructor() {
    this._ctx = null;
    this._masterGain = null;
    this._musicGain = null;
    this._sfxGain = null;
    this._musicLoop = null;
    this._musicTimeout = null;
    this._currentTheme = null;
    this._muted = false;
    this._walkAccum = 0;
    this._lastOnGround = false;
  }

  /**
   * Call from a direct user gesture (click/keydown handler).
   * Creates the AudioContext if needed, then RESUMES it.
   * Returns a promise that resolves when audio is running.
   */
  async init() {
    if (!this._ctx) {
      try {
        this._ctx = new (window.AudioContext || window.webkitAudioContext)();

        this._masterGain = this._ctx.createGain();
        this._masterGain.gain.value = 0.7;
        this._masterGain.connect(this._ctx.destination);

        this._musicGain = this._ctx.createGain();
        this._musicGain.gain.value = 0.28;
        this._musicGain.connect(this._masterGain);

        this._sfxGain = this._ctx.createGain();
        this._sfxGain.gain.value = 1.0;
        this._sfxGain.connect(this._masterGain);
      } catch (e) {
        console.warn('[SoundEngine] Web Audio not available:', e);
        return false;
      }
    }

    // Browser blocks audio until the context is explicitly resumed
    if (this._ctx.state === 'suspended') {
      try {
        await this._ctx.resume();
      } catch (e) {
        console.warn('[SoundEngine] Could not resume AudioContext:', e);
      }
    }

    return this._ctx.state === 'running';
  }

  get ctx() { return this._ctx; }
  get ready() { return !!this._ctx; }

  // ── Volume helpers ─────────────────────────────────────

  setMasterVolume(v) {
    if (!this.ready) return;
    this._masterGain.gain.linearRampToValueAtTime(v, this._ctx.currentTime + 0.05);
  }
  setMusicVolume(v) {
    if (!this.ready) return;
    this._musicGain.gain.linearRampToValueAtTime(v, this._ctx.currentTime + 0.05);
  }
  toggleMute() {
    this._muted = !this._muted;
    this.setMasterVolume(this._muted ? 0 : 0.7);
    return this._muted;
  }

  // ── Low-level synth helpers ────────────────────────────

  /** Play a tone with ADSR envelope */
  _tone(freq, duration, opts = {}) {
    if (!this.ready) return;
    const { type = 'square', attack = 0.01, decay = 0.1, sustain = 0.5,
            release = 0.2, volume = 0.3, dest = this._sfxGain,
            detune = 0, freqEnd = null } = opts;

    const now = this._ctx.currentTime;
    const osc = this._ctx.createOscillator();
    const gain = this._ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    osc.detune.setValueAtTime(detune, now);
    if (freqEnd !== null) {
      osc.frequency.linearRampToValueAtTime(freqEnd, now + duration);
    }

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + attack);
    gain.gain.linearRampToValueAtTime(volume * sustain, now + attack + decay);
    gain.gain.setValueAtTime(volume * sustain, now + duration - release);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(now);
    osc.stop(now + duration + 0.01);
  }

  /** White noise burst */
  _noise(duration, opts = {}) {
    if (!this.ready) return;
    const { volume = 0.2, attack = 0.005, release = 0.15,
            lowpass = 800, dest = this._sfxGain } = opts;

    const now = this._ctx.currentTime;
    const bufferSize = Math.ceil(this._ctx.sampleRate * duration);
    const buffer = this._ctx.createBuffer(1, bufferSize, this._ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const src = this._ctx.createBufferSource();
    src.buffer = buffer;

    const filter = this._ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = lowpass;

    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + attack);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    src.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    src.start(now);
    src.stop(now + duration + 0.01);
  }

  /** Schedule a melody (array of [freq, duration, delay] tuples) */
  _melody(notes, opts = {}) {
    if (!this.ready) return;
    let t = 0;
    for (const [freq, dur, gap = 0] of notes) {
      const delay = t;
      setTimeout(() => this._tone(freq, dur, opts), delay * 1000);
      t += dur + gap;
    }
  }

  // ── Sound Effects ──────────────────────────────────────

  jump() {
    this._tone(220, 0.12, { type: 'square', freqEnd: 440, volume: 0.22,
      attack: 0.005, decay: 0.05, sustain: 0.6, release: 0.05 });
    this._tone(330, 0.08, { type: 'sine', freqEnd: 550, volume: 0.1,
      attack: 0.005, decay: 0.05, sustain: 0.3, release: 0.03 });
  }

  land() {
    this._noise(0.12, { volume: 0.18, lowpass: 300, attack: 0.002, release: 0.1 });
    this._tone(80, 0.1, { type: 'sine', volume: 0.25, attack: 0.002,
      decay: 0.05, sustain: 0.1, release: 0.04 });
  }

  walk() {
    this._noise(0.055, { volume: 0.07, lowpass: 500, attack: 0.002, release: 0.04 });
    this._tone(110, 0.04, { type: 'sine', volume: 0.06, attack: 0.002,
      decay: 0.02, sustain: 0.1, release: 0.01 });
  }

  /** Magical shimmer when a word is spawned */
  spawn(config) {
    if (!this.ready) return;
    // Base notes depend on entity type
    const typeNotes = {
      platform: [523, 659, 784],
      creature: [440, 554, 659, 880],
      vehicle:  [392, 523, 659],
      item:     [659, 784, 1047],
      food:     [523, 659, 784, 1047],
      hazard:   [220, 185, 147],
    };
    const notes = typeNotes[config?.type] ?? [523, 659, 784];
    // Ascending sparkle
    notes.forEach((freq, i) => {
      setTimeout(() => {
        this._tone(freq, 0.18, { type: 'sine', volume: 0.18,
          attack: 0.01, decay: 0.08, sustain: 0.4, release: 0.08 });
        this._tone(freq * 2, 0.1, { type: 'sine', volume: 0.08,
          attack: 0.005, decay: 0.05, sustain: 0.2, release: 0.04 });
      }, i * 60);
    });
    // Sparkle noise
    this._noise(0.15, { volume: 0.06, lowpass: 3000 });
  }

  /** For modifiers like giant, tiny, explosive etc */
  spawnModified(modLabel) {
    if (!this.ready) return;
    if (modLabel?.includes('GIANT') || modLabel?.includes('HUGE')) {
      // Deep boom
      this._tone(55, 0.4, { type: 'sine', volume: 0.4, freqEnd: 35,
        attack: 0.02, decay: 0.2, sustain: 0.3, release: 0.15 });
      this._noise(0.3, { volume: 0.15, lowpass: 200 });
    } else if (modLabel?.includes('TINY') || modLabel?.includes('MINI')) {
      // High squeak
      this._tone(1200, 0.12, { type: 'sine', volume: 0.15, freqEnd: 800,
        attack: 0.005, decay: 0.05, sustain: 0.3, release: 0.05 });
    } else if (modLabel?.includes('EXPLOSIVE')) {
      this._tone(220, 0.15, { type: 'sawtooth', volume: 0.2, freqEnd: 110,
        attack: 0.005, decay: 0.1, sustain: 0.2, release: 0.03 });
    } else if (modLabel?.includes('RAINBOW')) {
      // Glittery ascending
      [523, 659, 784, 1047, 1319].forEach((f, i) =>
        setTimeout(() => this._tone(f, 0.1, { type: 'sine', volume: 0.12 }), i * 40));
    }
  }

  collect() {
    // Bright rising ding
    this._tone(880, 0.08, { type: 'sine', volume: 0.25,
      attack: 0.005, decay: 0.04, sustain: 0.5, release: 0.03 });
    this._tone(1320, 0.12, { type: 'sine', volume: 0.18,
      attack: 0.005, decay: 0.06, sustain: 0.4, release: 0.05 });
    this._tone(1760, 0.15, { type: 'sine', volume: 0.12,
      attack: 0.005, decay: 0.08, sustain: 0.3, release: 0.06 });
  }

  pearl() {
    // Watery collect sound
    this._tone(1047, 0.1, { type: 'sine', volume: 0.2,
      attack: 0.005, decay: 0.05, sustain: 0.4, release: 0.04 });
    this._tone(1319, 0.15, { type: 'sine', volume: 0.15,
      attack: 0.005, decay: 0.07, sustain: 0.3, release: 0.06 });
    this._noise(0.08, { volume: 0.04, lowpass: 5000 });
  }

  hurt() {
    // Buzzy hit
    this._tone(220, 0.25, { type: 'sawtooth', freqEnd: 110, volume: 0.3,
      attack: 0.003, decay: 0.1, sustain: 0.3, release: 0.1 });
    this._noise(0.15, { volume: 0.12, lowpass: 600 });
  }

  die() {
    // Sad descending melody
    const notes = [[392,0.12,0.02],[330,0.12,0.02],[262,0.12,0.02],
                   [220,0.2,0.02],[175,0.3,0]];
    notes.forEach(([f, d, g], i) => {
      let t = 0;
      for (let j = 0; j < i; j++) t += notes[j][1] + notes[j][2];
      setTimeout(() => this._tone(f, d, { type: 'square', volume: 0.25,
        attack: 0.01, decay: 0.05, sustain: 0.6, release: 0.1 }), t * 1000);
    });
  }

  explode() {
    // Big noise burst + low boom
    this._noise(0.5, { volume: 0.4, lowpass: 1200, attack: 0.002, release: 0.4 });
    this._noise(0.3, { volume: 0.3, lowpass: 200, attack: 0.001, release: 0.28 });
    this._tone(60, 0.4, { type: 'sine', volume: 0.5, freqEnd: 30,
      attack: 0.002, decay: 0.15, sustain: 0.2, release: 0.2 });
    this._tone(120, 0.2, { type: 'sine', volume: 0.3, freqEnd: 60,
      attack: 0.001, decay: 0.1, sustain: 0.1, release: 0.08 });
  }

  splash() {
    this._noise(0.3, { volume: 0.18, lowpass: 2000, attack: 0.003, release: 0.25 });
    this._tone(300, 0.12, { type: 'sine', freqEnd: 150, volume: 0.12,
      attack: 0.003, decay: 0.08, sustain: 0.1, release: 0.05 });
    this._tone(200, 0.15, { type: 'sine', freqEnd: 100, volume: 0.1,
      attack: 0.003, decay: 0.1, sustain: 0.1, release: 0.05 });
  }

  levelComplete() {
    // Triumphant fanfare
    const melody = [
      [523, 0.12, 0.02], [659, 0.12, 0.02], [784, 0.12, 0.02],
      [1047,0.35, 0.06],
      [880, 0.12, 0.02], [1047,0.12, 0.02], [1175,0.12, 0.02],
      [1319,0.5,  0],
    ];
    let t = 0;
    for (const [f, d, g] of melody) {
      const delay = t;
      setTimeout(() => {
        this._tone(f,   d, { type: 'square', volume: 0.28, attack: 0.01,
          decay: 0.08, sustain: 0.7, release: 0.08 });
        this._tone(f*2, d, { type: 'sine',   volume: 0.1,  attack: 0.01,
          decay: 0.06, sustain: 0.5, release: 0.06 });
      }, delay * 1000);
      t += d + g;
    }
    // Sparkle
    setTimeout(() => this._noise(0.4, { volume: 0.08, lowpass: 4000 }), 800);
  }

  gameOver() {
    const melody = [
      [392,0.18,0.04],[330,0.18,0.04],[262,0.18,0.04],
      [220,0.45,0.06],[196,0.6,0],
    ];
    let t = 0;
    for (const [f, d, g] of melody) {
      const delay = t;
      setTimeout(() => this._tone(f, d, { type: 'sawtooth', volume: 0.22,
        attack: 0.01, decay: 0.1, sustain: 0.5, release: 0.1 }), delay * 1000);
      t += d + g;
    }
  }

  lavaWarning() {
    // Urgent alarm tone
    this._tone(880, 0.08, { type: 'square', volume: 0.2,
      attack: 0.003, decay: 0.02, sustain: 0.8, release: 0.02 });
    setTimeout(() => this._tone(660, 0.08, { type: 'square', volume: 0.2,
      attack: 0.003, decay: 0.02, sustain: 0.8, release: 0.02 }), 150);
  }

  wind() {
    this._noise(0.4, { volume: 0.06, lowpass: 600, attack: 0.1, release: 0.3 });
  }

  npcTalk() {
    // Quick bleep-bloop voice
    const baseFreq = 300 + Math.random() * 200;
    [0, 60, 120].forEach(delay =>
      setTimeout(() => this._tone(
        baseFreq + Math.random() * 100,
        0.06,
        { type: 'square', volume: 0.1,
          attack: 0.003, decay: 0.02, sustain: 0.6, release: 0.02 }
      ), delay)
    );
  }

  buttonClick() {
    this._tone(660, 0.06, { type: 'sine', volume: 0.15,
      attack: 0.003, decay: 0.03, sustain: 0.5, release: 0.02 });
    this._tone(880, 0.08, { type: 'sine', volume: 0.10,
      attack: 0.003, decay: 0.04, sustain: 0.4, release: 0.03 });
  }

  errorBeep() {
    this._tone(220, 0.15, { type: 'square', volume: 0.2,
      attack: 0.003, decay: 0.05, sustain: 0.4, release: 0.08 });
    setTimeout(() =>
      this._tone(185, 0.15, { type: 'square', volume: 0.15,
        attack: 0.003, decay: 0.05, sustain: 0.4, release: 0.08 }), 100);
  }

  // ── Walk tick manager ──────────────────────────────────

  /** Call every frame with dt, player vx, onGround */
  tickWalk(dt, vx, onGround) {
    if (!onGround || Math.abs(vx) < 20) { this._walkAccum = 0; return; }
    this._walkAccum += dt;
    if (this._walkAccum > 0.22) {
      this._walkAccum = 0;
      this.walk();
    }
    // Land event
    if (onGround && !this._lastOnGround) this.land();
    this._lastOnGround = onGround;
  }

  // ── Background Music ───────────────────────────────────

  /**
   * Start procedural background music for a level theme.
   * themes: 'beach' | 'reef' | 'temple' | 'volcano' | 'sky' | 'menu'
   */
  startMusic(theme) {
    if (!this.ready) return;
    this.stopMusic();
    this._currentTheme = theme;
    this._playMusicTheme(theme);
  }

  stopMusic() {
    if (this._musicTimeout) {
      clearTimeout(this._musicTimeout);
      this._musicTimeout = null;
    }
    this._currentTheme = null;
  }

  _playMusicTheme(theme) {
    if (!this.ready || this._currentTheme !== theme) return;
    const themes = {
      menu:    this._musicMenu.bind(this),
      beach:   this._musicBeach.bind(this),
      reef:    this._musicReef.bind(this),
      temple:  this._musicTemple.bind(this),
      volcano: this._musicVolcano.bind(this),
      sky:     this._musicSky.bind(this),
    };
    const fn = themes[theme] ?? themes.beach;
    const duration = fn();
    // Loop
    this._musicTimeout = setTimeout(() => this._playMusicTheme(theme), duration * 1000);
  }

  // Each music function returns its duration in seconds

  _musicMenu() {
    // Gentle, cheerful waltz
    const scale = [523, 587, 659, 698, 784, 880, 988, 1047];
    const pattern = [0,2,4,5,4,2,0,2,4,7,5,4,2,0,-1,0,2,4,5,7,5,4,2,4];
    let t = 0;
    for (const degree of pattern) {
      const freq = degree === -1 ? 0 : scale[Math.abs(degree) % scale.length];
      if (freq > 0) {
        const delay = t;
        const f = freq;
        setTimeout(() => {
          this._tone(f, 0.3, { type: 'sine', volume: 0.12, dest: this._musicGain,
            attack: 0.02, decay: 0.1, sustain: 0.6, release: 0.1 });
          this._tone(f/2, 0.3, { type: 'triangle', volume: 0.06, dest: this._musicGain,
            attack: 0.02, decay: 0.1, sustain: 0.5, release: 0.1 });
        }, delay * 1000);
      }
      t += 0.28;
    }
    return t + 0.3;
  }

  _musicBeach() {
    // Happy C major arpeggio
    const chords = [
      [523, 659, 784],   // C maj
      [523, 659, 784],
      [493, 622, 740],   // B diminished
      [523, 659, 784],
      [440, 523, 659],   // A min
      [440, 523, 659],
      [392, 494, 587],   // G maj
      [392, 494, 587],
    ];
    let t = 0;
    for (const chord of chords) {
      chord.forEach((freq, i) => {
        const delay = t + i * 0.12;
        const f = freq;
        setTimeout(() => {
          this._tone(f, 0.35, { type: 'sine', volume: 0.10, dest: this._musicGain,
            attack: 0.02, decay: 0.1, sustain: 0.6, release: 0.12 });
        }, delay * 1000);
      });
      // Bass note
      const bassDelay = t;
      const bass = chord[0] / 2;
      setTimeout(() => {
        this._tone(bass, 0.55, { type: 'triangle', volume: 0.09, dest: this._musicGain,
          attack: 0.03, decay: 0.15, sustain: 0.5, release: 0.2 });
      }, bassDelay * 1000);
      t += 0.55;
    }
    // Melody
    const melody = [784,880,1047,880,784,659,784,659,523,659,784,659];
    melody.forEach((f, i) => {
      setTimeout(() => {
        this._tone(f, 0.22, { type: 'triangle', volume: 0.08, dest: this._musicGain,
          attack: 0.01, decay: 0.08, sustain: 0.5, release: 0.1 });
      }, i * 0.37 * 1000);
    });
    return t + 0.55;
  }

  _musicReef() {
    // Watery, mysterious A minor
    const notes = [440,523,587,659,523,494,440,392,440,523,659,784,659,587,523,440];
    let t = 0;
    notes.forEach((f, i) => {
      const delay = t;
      setTimeout(() => {
        this._tone(f, 0.6, { type: 'sine', volume: 0.09, dest: this._musicGain,
          attack: 0.05, decay: 0.15, sustain: 0.5, release: 0.25,
          detune: Math.sin(i) * 5 });
        if (i % 4 === 0) {
          this._tone(f/2, 0.8, { type: 'triangle', volume: 0.07, dest: this._musicGain,
            attack: 0.05, decay: 0.2, sustain: 0.4, release: 0.3 });
        }
      }, delay * 1000);
      t += 0.45;
    });
    // Bubble noise texture
    for (let b = 0; b < 8; b++) {
      setTimeout(() => this._noise(0.08, { volume: 0.02, lowpass: 800, dest: this._musicGain }),
        b * 900 + Math.random() * 400);
    }
    return t + 0.8;
  }

  _musicTemple() {
    // Ancient pentatonic, D minor
    const pent = [294, 349, 392, 440, 523, 587, 659, 698];
    const pattern = [0,3,5,3,0,2,4,2,0,5,3,0,2,4,5,4,3,2,0];
    let t = 0;
    pattern.forEach((degree, i) => {
      const f = pent[degree % pent.length];
      const delay = t;
      setTimeout(() => {
        this._tone(f, 0.5, { type: 'triangle', volume: 0.1, dest: this._musicGain,
          attack: 0.04, decay: 0.15, sustain: 0.5, release: 0.25 });
        if (i % 4 === 0) {
          this._tone(f/4, 0.6, { type: 'sine', volume: 0.08, dest: this._musicGain,
            attack: 0.05, decay: 0.2, sustain: 0.4, release: 0.3 });
        }
      }, delay * 1000);
      t += i % 3 === 2 ? 0.7 : 0.42;
    });
    return t + 0.6;
  }

  _musicVolcano() {
    // Tense, urgent — fast pulse with rising tension
    const danger = [110,117,123,131,139,147,156,165,175,185,196,208];
    let t = 0;
    danger.forEach((f, i) => {
      const delay = t;
      setTimeout(() => {
        this._tone(f, 0.18, { type: 'sawtooth', volume: 0.12, dest: this._musicGain,
          attack: 0.005, decay: 0.08, sustain: 0.4, release: 0.08 });
        this._tone(f*1.5, 0.12, { type: 'square', volume: 0.07, dest: this._musicGain,
          attack: 0.005, decay: 0.06, sustain: 0.3, release: 0.05 });
        if (i % 3 === 0) {
          this._noise(0.1, { volume: 0.05, lowpass: 400, dest: this._musicGain });
        }
      }, delay * 1000);
      t += 0.22;
    });
    // Rumble
    for (let r = 0; r < 5; r++) {
      setTimeout(() =>
        this._noise(0.3, { volume: 0.04, lowpass: 150, dest: this._musicGain }),
        r * 500);
    }
    return t + 0.25;
  }

  _musicSky() {
    // Ethereal, dreamy — high sparkly notes
    const sky = [1047,1175,1319,1568,1760,1976,2093];
    const slow = [523, 587, 659, 784, 880, 988, 1047];
    let t = 0;
    // Slow arpeggio underneath
    slow.forEach((f, i) => {
      setTimeout(() => {
        this._tone(f, 1.2, { type: 'sine', volume: 0.08, dest: this._musicGain,
          attack: 0.15, decay: 0.3, sustain: 0.5, release: 0.4 });
      }, i * 900);
    });
    // Sparkle melody on top
    const sparkPattern = [0,2,4,6,5,3,1,0,2,5,4,2,0,3,5,6,4,2,0];
    sparkPattern.forEach((degree, i) => {
      const f = sky[degree % sky.length];
      const delay = i * 0.38;
      setTimeout(() => {
        this._tone(f, 0.25, { type: 'sine', volume: 0.06, dest: this._musicGain,
          attack: 0.02, decay: 0.08, sustain: 0.3, release: 0.12 });
      }, delay * 1000);
      t = Math.max(t, delay + 0.25);
    });
    return Math.max(t, slow.length * 0.9) + 1.2;
  }
}

// Singleton
export const sound = new SoundEngine();
